import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';

interface VendorJobCountsByStatus {
  awarded: number;
  in_progress: number;
  completed_pending_review: number;
  accepted: number;
  rejected: number;
  disputed: number;
  closed: number;
}

interface MilestoneWithJob {
  id: string;
  name: string;
  due_date: string;
  is_completed: boolean;
  action_owner: 'client' | 'vendor';
  job_id: string;
  job_title: string;
  job_public_id: string;
  job_status: string;
}

interface VendorDashboardData {
  jobCountsByStatus: VendorJobCountsByStatus;
  activeBidsCount: number;
  wonJobsCount: number;
  totalEarned: number;
  winRate: number;
  currency: string;
  upcomingMilestones: MilestoneWithJob[];
  loading: boolean;
  error: string | null;
}

export const useVendorDashboardData = (): VendorDashboardData => {
  const { user } = useAuth();

  // Fetch vendor's job data using direct database joins
  const { data: jobCounts, isLoading: jobCountsLoading, error: jobCountsError } = useQuery({
    queryKey: ['vendor-dashboard-job-counts', user?.id],
    queryFn: async () => {
      if (!user) throw new Error('User not authenticated');

      // Per requirements: Dashboard metrics show only personal metrics for all roles
      // Step 1: Get vendor's won jobs directly via JOIN on awards -> bids -> jobs
      const { data: vendorAwards, error: wonJobsError } = await supabase
        .from('awards')
        .select(`
          job_id,
          total_award_amount_cents,
          jobs!inner(
            id,
            status,
            budget_currency
          ),
          bids!fk_awards_winning_bid_id!inner(
            vendor_user_id
          )
        `)
        .eq('bids.vendor_user_id', user.id);

      if (wonJobsError) throw wonJobsError;

      // Extract job data from the structure
      const vendorJobs = (vendorAwards || [])
        .map(award => ({
          job_id: award.job_id,
          status: award.jobs?.status,
          award_amount_cents: award.total_award_amount_cents,
          currency: award.jobs?.budget_currency || 'USD',
        }))
        .filter(j => j.status); // Only jobs with valid status

      // Initialize counts
      const counts: VendorJobCountsByStatus = {
        awarded: 0,
        in_progress: 0,
        completed_pending_review: 0,
        accepted: 0,
        rejected: 0,
        disputed: 0,
        closed: 0,
      };

      let totalEarned = 0;
      const currency = 'USD';

      // Count by status and calculate earnings
      vendorJobs.forEach((job) => {
        const status = job.status as keyof VendorJobCountsByStatus;
        if (counts[status] !== undefined) {
          counts[status]++;
        }

        // Calculate earnings for completed jobs
        if (['accepted', 'closed'].includes(status)) {
          totalEarned += job.award_amount_cents || 0;
        }
      });

      const wonJobsCount = vendorJobs.length;

      // Step 2: Calculate active bids (bids on open RFQs, not yet awarded)
      const { data: allBids, error: allBidsError } = await supabase
        .from('bids')
        .select(`
          id,
          rfq_id,
          rfqs!inner(
            id,
            job_id,
            jobs!inner(
              status
            )
          )
        `)
        .eq('vendor_user_id', user.id);

      if (allBidsError) throw allBidsError;

      // Count bids on open RFQs that haven't been awarded yet
      const wonJobIds = new Set(vendorJobs.map(j => j.job_id));
      const activeBidsCount = (allBids || [])
        .filter(bid => 
          bid.rfqs?.jobs?.status === 'rfq_open' && 
          !wonJobIds.has(bid.rfqs?.job_id)
        ).length;

      // Step 3: Calculate win rate
      // Opportunities = distinct jobs bid on (excluding rfq_open)
      const allJobIdsBidOn = new Set(
        (allBids || [])
          .filter(bid => bid.rfqs?.jobs?.status !== 'rfq_open')
          .map(bid => bid.rfqs?.job_id)
          .filter(Boolean)
      );
      const opportunities = allJobIdsBidOn.size;
      let winRate = opportunities > 0 ? (wonJobsCount / opportunities) * 100 : 0;
      winRate = Math.min(100, Math.max(0, winRate)); // Clamp to [0, 100]

      return { counts, activeBidsCount, wonJobsCount, totalEarned, winRate, currency };
    },
    enabled: !!user,
  });

  const { data: milestones, isLoading: milestonesLoading, error: milestonesError } = useQuery({
    queryKey: ['vendor-dashboard-upcoming-milestones', user?.id],
    queryFn: async () => {
      if (!user) throw new Error('User not authenticated');

      const todayIso = new Date().toISOString();

      // Get vendor's won job IDs directly
      const { data: vendorAwards, error: wonJobsError } = await supabase
        .from('awards')
        .select(`
          job_id,
          bids!fk_awards_winning_bid_id!inner(
            vendor_user_id
          )
        `)
        .eq('bids.vendor_user_id', user.id);

      if (wonJobsError) throw wonJobsError;

      const vendorWonJobIds = Array.from(new Set(
        (vendorAwards || []).map(award => award.job_id)
      )).filter(Boolean) as string[];

      if (vendorWonJobIds.length === 0) return [] as MilestoneWithJob[];

      // Fetch milestones WITHOUT joining to avoid RLS recursion
      const { data: milestoneRows, error: milestonesError } = await supabase
        .from('milestones')
        .select('*')
        .in('job_id', vendorWonJobIds)
        .eq('is_blank', false)
        .eq('is_completed', false)
        .gte('due_date', todayIso)
        .order('due_date', { ascending: true })
        .limit(5);

      if (milestonesError) throw milestonesError;
      if (!milestoneRows || milestoneRows.length === 0) return [] as MilestoneWithJob[];

      // Fetch job details separately
      const milestoneJobIds = Array.from(new Set(milestoneRows.map((m: any) => m.job_id)));
      const { data: jobRows, error: jobsError } = await supabase
        .from('jobs')
        .select('id, title, public_id, status')
        .in('id', milestoneJobIds);

      if (jobsError) throw jobsError;

      const jobsById = new Map((jobRows || []).map((j: any) => [j.id, j]));

      const enriched = (milestoneRows || [])
        .map((m: any) => {
          const job = jobsById.get(m.job_id);
          if (!job) return null;
          return {
            id: m.id,
            name: (m as any).milestone_name ?? (m as any).name ?? 'Milestone',
            due_date: m.due_date,
            is_completed: m.is_completed,
            action_owner: m.action_owner,
            job_id: m.job_id,
            job_title: job.title,
            job_public_id: job.public_id,
            job_status: job.status,
          } as MilestoneWithJob;
        })
        .filter(Boolean)
        .slice(0, 5) as MilestoneWithJob[];

      return enriched;
    },
    enabled: !!user,
  });

  return {
    jobCountsByStatus: jobCounts?.counts || {
      awarded: 0,
      in_progress: 0,
      completed_pending_review: 0,
      accepted: 0,
      rejected: 0,
      disputed: 0,
      closed: 0,
    },
    activeBidsCount: jobCounts?.activeBidsCount || 0,
    wonJobsCount: jobCounts?.wonJobsCount || 0,
    totalEarned: jobCounts?.totalEarned || 0,
    winRate: jobCounts?.winRate || 0,
    currency: jobCounts?.currency || 'USD',
    upcomingMilestones: milestones || [],
    loading: jobCountsLoading || milestonesLoading,
    error: jobCountsError?.message || milestonesError?.message || null,
  };
};
